import { useState, useMemo, useCallback } from "react";
import { useHR } from "../context/HRContext";
import toast from "react-hot-toast";

export function useEmployees() {
  const { employees, addEmployee, updateEmployee, toggleEmployeeStatus, deleteEmployee } = useHR();

  const [search, setSearch] = useState("");
  const [filterDept, setFilterDept] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [viewingEmployee, setViewingEmployee] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "", lastName: "", email: "", phone: "", employeeId: "", department: "", role: "",
    designation: "", employmentType: "full_time", joiningDate: "",
    workLocation: "", reportingManager: "", salary: "", notes: "", password: "", companyName: "", documents: [],
    gender: "", religion: "", dob: "", bloodGroup: "", experienceYears: "", previousCompany: ""
  });
  const [formErrors, setFormErrors] = useState({});

  const filtered = useMemo(() => {
    return employees.filter((e) => {
      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        e.name?.toLowerCase().includes(q) ||
        e.email?.toLowerCase().includes(q) ||
        e.id?.toLowerCase().includes(q) ||
        e.designation?.toLowerCase().includes(q);
      const matchDept = !filterDept || e.department === filterDept;
      const matchStatus = !filterStatus || e.status === filterStatus;
      const matchType = !filterType || e.employmentType === filterType;
      return matchSearch && matchDept && matchStatus && matchType;
    });
  }, [employees, search, filterDept, filterStatus, filterType]);

  const stats = useMemo(() => ({
    total: employees.length,
    active: employees.filter((e) => e.status === "active").length,
    probation: employees.filter((e) => e.status === "probation").length,
    inactive: employees.filter((e) => e.status === "inactive").length,
  }), [employees]);

  const validate = useCallback((data, isEditing) => {
    const errs = {};
    if (!data.firstName?.trim()) errs.firstName = "First name is required";
    if (!data.lastName?.trim()) errs.lastName = "Last name is required";
    if (!data.employeeId?.trim()) errs.employeeId = "Employee ID is required";
    if (!data.email?.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) errs.email = "Invalid email";
    if (!data.phone?.trim()) errs.phone = "Phone is required";
    else if (!/^\d{10}$/.test(data.phone)) errs.phone = "Phone must be exactly 10 digits";
    if (!isEditing && !data.password?.trim()) {
      errs.password = "Password is required";
    } else if (data.password?.trim()) {
      const p = data.password.trim();
      if (p.length < 8) errs.password = "Must be at least 8 characters";
      else if (!/[A-Z]/.test(p)) errs.password = "Must contain at least 1 uppercase letter";
      else if (!/[a-z]/.test(p)) errs.password = "Must contain at least 1 lowercase letter";
      else if (!/[0-9]/.test(p)) errs.password = "Must contain at least 1 number";
      else if (!/[^A-Za-z0-9]/.test(p)) errs.password = "Must contain at least 1 special character";
    }
    if (!data.department) errs.department = "Department is required";
    if (!data.role) errs.role = "Role is required";
    if (!data.designation?.trim()) errs.designation = "Designation is required";
    if (!data.joiningDate) errs.joiningDate = "Joining date is required";
    if (!data.workLocation?.trim()) errs.workLocation = "Work location is required";
    if (!data.salary || isNaN(Number(data.salary))) errs.salary = "Valid CTC is required";
    return errs;
  }, []);

  const openCreate = useCallback(() => {
    setEditingEmployee(null);
    setFormData({
      firstName: "", lastName: "", email: "", phone: "", employeeId: "", department: "", role: "",
      designation: "", employmentType: "full_time", joiningDate: "",
      workLocation: "", reportingManager: "", salary: "", notes: "", password: "", companyName: "", documents: [],
      gender: "", religion: "", dob: "", bloodGroup: "", experienceYears: "", previousCompany: ""
    });
    setFormErrors({});
    setIsFormOpen(true);
  }, []);

  const openEdit = useCallback((emp) => {
    setEditingEmployee(emp);
    
    // Split name back to firstName and lastName if they don't exist
    const nameParts = (emp.name || "").split(" ");
    const fName = emp.firstName || nameParts[0] || "";
    const lName = emp.lastName || nameParts.slice(1).join(" ") || "";
    
    setFormData({ 
      ...emp,
      firstName: fName,
      lastName: lName,
      password: "" // Don't populate password on edit
    });
    setFormErrors({});
    setIsFormOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    setIsFormOpen(false);
    setEditingEmployee(null);
    setFormErrors({});
  }, []);

  const updateField = useCallback((field, value) => {
    setFormData((p) => ({ ...p, [field]: value }));
    setFormErrors((p) => ({ ...p, [field]: undefined }));
  }, []);

  const save = useCallback(async () => {
    const errs = validate(formData, !!editingEmployee);
    if (Object.keys(errs).length) { setFormErrors(errs); return false; }
    setIsSaving(true);
    try {
      await new Promise((r) => setTimeout(r, 400));
      const payload = new FormData();
      
      // Append standard fields
      Object.keys(formData).forEach(key => {
        if (key === 'documents' || key === 'salary' || key === 'employmentType' || key === 'companyName') return;
        if (formData[key] !== undefined && formData[key] !== null && formData[key] !== "") {
          payload.append(key, formData[key]);
        }
      });
      
      // Append formatted fields
      payload.append('salary', Number(formData.salary));
      payload.append('employmentType', formData.employmentType || "full_time");
      payload.append('companyName', formData.companyName || "Solar");

      // Handle documents separation
      if (formData.documents && formData.documents.length > 0) {
        const existingDocs = [];
        formData.documents.forEach(doc => {
          if (doc.file) {
            payload.append('documents', doc.file);
          } else {
            existingDocs.push({ name: doc.name, url: doc.url });
          }
        });
        if (existingDocs.length > 0) {
          payload.append('existingDocuments', JSON.stringify(existingDocs));
        }
      }
      if (editingEmployee) {
        await updateEmployee(editingEmployee._id || editingEmployee.id, payload);
      } else {
        await addEmployee(payload);
      }
      toast.success("Employee details successfully saved");
      closeForm();
      return true;
    } catch (error) {
      console.error("Save employee error:", error);
      const msg = error.response?.data?.message || error.message || "An error occurred while saving employee";
      toast.error(msg);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [formData, editingEmployee, validate, updateEmployee, addEmployee, closeForm]);

  return {
    employees: filtered, allEmployees: employees,
    search, setSearch, filterDept, setFilterDept,
    filterStatus, setFilterStatus, filterType, setFilterType,
    stats, isFormOpen, editingEmployee, viewingEmployee,
    setViewingEmployee, formData, formErrors, isSaving,
    openCreate, openEdit, closeForm, updateField, save,
    toggleEmployeeStatus, deleteEmployee,
  };
}
